import { formatCssHex, parseHexToRgb, type QrStyleConfig } from './qr-style-rules.js';

/**
 * Supported QR export file formats across both GS1 print and styled QR versions.
 */
export type QrExportFormat = 'SVG' | 'PDF' | 'PNG' | 'EPS';

export const QR_EXPORT_FORMATS: readonly QrExportFormat[] = ['SVG', 'PDF', 'PNG', 'EPS'] as const;

export type QrExportMimeType =
  'image/svg+xml' | 'application/pdf' | 'image/png' | 'application/postscript';

export function getQrExportMimeType(format: QrExportFormat): QrExportMimeType {
  switch (format) {
    case 'SVG':
      return 'image/svg+xml';
    case 'PDF':
      return 'application/pdf';
    case 'PNG':
      return 'image/png';
    case 'EPS':
      return 'application/postscript';
  }
}

/**
 * GS1 General Specifications Release 26.0 §5.12.3.1, Table 5-46 (p412):
 * X-dimension range for retail consumer items scanned at POS:
 * - Minimum: 0.396 mm
 * - Target (default): 0.495 mm
 * - Maximum: 0.990 mm
 */
export const QR_X_DIMENSION_MIN_MM = 0.396;
export const QR_X_DIMENSION_DEFAULT_MM = 0.495;
export const QR_X_DIMENSION_MAX_MM = 0.99;

export interface QrXDimensionOption {
  readonly value: number;
  readonly label: string;
}

export const QR_X_DIMENSION_OPTIONS: readonly QrXDimensionOption[] = [
  { value: 0.396, label: '0.396 mm — Minimum (small packaging)' },
  { value: 0.495, label: '0.495 mm — Default (standard retail)' },
  { value: 0.6, label: '0.600 mm — Medium packaging' },
  { value: 0.743, label: '0.743 mm — Large (outer cases min)' },
  { value: 0.85, label: '0.850 mm — Extra large packaging' },
  { value: 0.99, label: '0.990 mm — Maximum (0.990 mm)' },
] as const;

export function isFinderPatternModule(
  col: number,
  row: number,
  moduleCount: number,
  quietZoneModules: number,
): boolean {
  const minIdx = quietZoneModules;
  const maxIdx = moduleCount - quietZoneModules - 1;
  const inTopLeft = col >= minIdx && col <= minIdx + 6 && row >= minIdx && row <= minIdx + 6;
  const inTopRight = col >= maxIdx - 6 && col <= maxIdx && row >= minIdx && row <= minIdx + 6;
  const inBottomLeft = col >= minIdx && col <= minIdx + 6 && row >= maxIdx - 6 && row <= maxIdx;
  return inTopLeft || inTopRight || inBottomLeft;
}

function rgbToUnitTriple(hex: string): string {
  const { r, g, b } = parseHexToRgb(hex);
  return `${(r / 255).toFixed(4)} ${(g / 255).toFixed(4)} ${(b / 255).toFixed(4)}`;
}

/**
 * Builds an Encapsulated PostScript (`%!PS-Adobe-3.0 EPSF-3.0`) vector document
 * sized in physical millimetres (`1 mm = 72 / 25.4 pt`) while preserving the 4X quiet zone.
 */
export function buildVectorEpsDocument(params: {
  readonly url: string;
  readonly clauseRef: string;
  readonly xDimensionMm: number;
  readonly physicalSizeMm: number;
  readonly viewBoxSize: number;
  readonly svgPathD: string;
  readonly moduleCount?: number;
  readonly cells?: readonly { readonly col: number; readonly row: number }[];
  readonly style?: QrStyleConfig;
}): string {
  const pagePt = Number(((params.physicalSizeMm * 72) / 25.4).toFixed(4));
  const bboxInt = Math.max(1, Math.ceil(pagePt));
  const fgTriple = params.style ? rgbToUnitTriple(params.style.fgHex) : '0 0 0';
  const bgTriple = params.style ? rgbToUnitTriple(params.style.bgHex) : '1 1 1';

  const lines: string[] = [
    '%!PS-Adobe-3.0 EPSF-3.0',
    `%%Title: (GS1 Digital Link QR - ${params.url})`,
    '%%Creator: (MyPlayer GS1 Resolver bwip-js gs1dlqrcode)',
    `%%BoundingBox: 0 0 ${String(bboxInt)} ${String(bboxInt)}`,
    `%%HiResBoundingBox: 0 0 ${pagePt.toFixed(4)} ${pagePt.toFixed(4)}`,
    `%%For: (${params.clauseRef}; X=${params.xDimensionMm.toFixed(3)}mm; 4X quiet zone)`,
    '%%LanguageLevel: 2',
    '%%Pages: 1',
    '%%EndComments',
    'gsave',
    `${bgTriple} setrgbcolor`,
    `0 0 ${pagePt.toFixed(4)} ${pagePt.toFixed(4)} rectfill`,
    `${fgTriple} setrgbcolor`,
  ];

  const dotShape = params.style?.dotShape ?? 'square';
  const moduleCount = params.moduleCount ?? 0;
  const cells = params.cells ?? [];

  if (dotShape !== 'square' && moduleCount > 0 && cells.length > 0) {
    const modPt = pagePt / moduleCount;
    const quietZone = params.style?.quietZoneModules ?? 4;
    for (const cell of cells) {
      const xPt = cell.col * modPt;
      const yPt = (moduleCount - 1 - cell.row) * modPt;
      if (
        isFinderPatternModule(cell.col, cell.row, moduleCount, quietZone) ||
        dotShape === 'rounded'
      ) {
        lines.push(
          `${xPt.toFixed(4)} ${yPt.toFixed(4)} ${modPt.toFixed(4)} ${modPt.toFixed(4)} rectfill`,
        );
      } else {
        const cx = xPt + modPt * 0.5;
        const cy = yPt + modPt * 0.5;
        const r = modPt * 0.44;
        lines.push(`newpath ${cx.toFixed(4)} ${cy.toFixed(4)} ${r.toFixed(4)} 0 360 arc fill`);
      }
    }
  } else {
    const scale = pagePt / (params.viewBoxSize || 1);
    const tokens = params.svgPathD.match(/[MLZ]|-?\d+(?:\.\d+)?/gi) ?? [];
    lines.push('newpath');
    let idx = 0;
    while (idx < tokens.length) {
      const cmd = tokens[idx]?.toUpperCase();
      if (cmd === 'M' || cmd === 'L') {
        const rawX = Number(tokens[idx + 1]);
        const rawY = Number(tokens[idx + 2]);
        if (Number.isFinite(rawX) && Number.isFinite(rawY)) {
          const ptX = (rawX * scale).toFixed(4);
          const ptY = ((params.viewBoxSize - rawY) * scale).toFixed(4);
          lines.push(`${ptX} ${ptY} ${cmd === 'M' ? 'moveto' : 'lineto'}`);
        }
        idx += 3;
      } else if (cmd === 'Z') {
        lines.push('closepath');
        idx += 1;
      } else {
        idx += 1;
      }
    }
    lines.push('eofill');
  }

  lines.push('grestore', 'showpage', '%%EOF', '');
  return lines.join('\n');
}

/**
 * Renders the QR module grid (including the 4X quiet zone on all four sides) into an RGBA pixel buffer.
 */
export function rasterizeQrToRgba(params: {
  readonly moduleCount: number;
  readonly cells: readonly { readonly col: number; readonly row: number }[];
  readonly pixelsPerModule?: number;
  readonly style?: QrStyleConfig;
}): {
  readonly rgba: Uint8ClampedArray;
  readonly size: number;
} {
  const { moduleCount, cells, style } = params;
  const pixelsPerModule = params.pixelsPerModule ?? 8;
  const safeModules = Math.max(1, moduleCount);
  const size = safeModules * pixelsPerModule;
  const rgba = new Uint8ClampedArray(size * size * 4);
  const fg = style ? parseHexToRgb(style.fgHex) : { r: 0, g: 0, b: 0 };
  const bg = style ? parseHexToRgb(style.bgHex) : { r: 255, g: 255, b: 255 };
  const effectiveQuietZone = style?.quietZoneModules ?? 4;

  for (let i = 0; i < size * size; i += 1) {
    const offset = i * 4;
    rgba[offset] = bg.r;
    rgba[offset + 1] = bg.g;
    rgba[offset + 2] = bg.b;
    rgba[offset + 3] = 255;
  }

  const cellSet = new Set<string>();
  for (const c of cells) cellSet.add(`${String(c.col)},${String(c.row)}`);

  const logoFraction =
    style?.showLogo && style.logoScalePercent > 0 ? style.logoScalePercent / 100 : 0;
  const logoMinPx = size * ((1 - logoFraction) / 2);
  const logoMaxPx = size - logoMinPx;
  const dotShape = style?.dotShape ?? 'square';

  for (let py = 0; py < size; py += 1) {
    const row = Math.floor(py / pixelsPerModule);
    const localY = (py + 0.5) / pixelsPerModule - row;
    for (let px = 0; px < size; px += 1) {
      if (
        logoFraction > 0 &&
        px >= logoMinPx &&
        px <= logoMaxPx &&
        py >= logoMinPx &&
        py <= logoMaxPx
      ) {
        continue;
      }
      const col = Math.floor(px / pixelsPerModule);
      if (!cellSet.has(`${String(col)},${String(row)}`)) continue;
      const localX = (px + 0.5) / pixelsPerModule - col;
      const isFinder = isFinderPatternModule(col, row, safeModules, effectiveQuietZone);
      let insideDot = true;
      if (!isFinder && dotShape === 'dots') {
        const dx = localX - 0.5;
        const dy = localY - 0.5;
        insideDot = dx * dx + dy * dy <= 0.48 * 0.48;
      } else if (!isFinder && dotShape === 'rounded') {
        const r = 0.25;
        const qx = Math.max(Math.abs(localX - 0.5) - (0.5 - r), 0);
        const qy = Math.max(Math.abs(localY - 0.5) - (0.5 - r), 0);
        insideDot = qx * qx + qy * qy <= r * r;
      }
      if (insideDot) {
        const idx = (py * size + px) * 4;
        rgba[idx] = fg.r;
        rgba[idx + 1] = fg.g;
        rgba[idx + 2] = fg.b;
        rgba[idx + 3] = 255;
      }
    }
  }

  return { rgba, size };
}

const CRC32_TABLE: readonly number[] = (() => {
  const table: number[] = [];
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) !== 0 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table.push(c >>> 0);
  }
  return table;
})();

function computeCrc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i] ?? 0;
    const tableVal = CRC32_TABLE[(c ^ b) & 0xff] ?? 0;
    c = tableVal ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function computeAdler32(bytes: Uint8Array): number {
  let a = 1;
  let b = 0;
  for (let i = 0; i < bytes.length; i += 1) {
    a = (a + (bytes[i] ?? 0)) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
}

function writeUint32BE(target: Uint8Array, offset: number, value: number): void {
  target[offset] = (value >>> 24) & 0xff;
  target[offset + 1] = (value >>> 16) & 0xff;
  target[offset + 2] = (value >>> 8) & 0xff;
  target[offset + 3] = value & 0xff;
}

function makePngChunk(typeAscii: string, data: Uint8Array): Uint8Array {
  const chunk = new Uint8Array(12 + data.length);
  writeUint32BE(chunk, 0, data.length);
  for (let i = 0; i < 4; i += 1) {
    chunk[4 + i] = typeAscii.charCodeAt(i) & 0xff;
  }
  chunk.set(data, 8);
  const crc = computeCrc32(chunk.subarray(4, 8 + data.length));
  writeUint32BE(chunk, 8 + data.length, crc);
  return chunk;
}

/**
 * Encodes an RGBA pixel buffer into a valid ISO/IEC 15948 PNG (`Uint8Array`) without requiring Node `Buffer`.
 */
export function encodeRgbaToPngBytes(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): Uint8Array {
  const rowStride = width * 4 + 1;
  const rawScanlines = new Uint8Array(rowStride * height);
  for (let y = 0; y < height; y += 1) {
    const rowStart = y * rowStride;
    rawScanlines[rowStart] = 0; // Filter type 0 (None)
    const srcStart = y * width * 4;
    rawScanlines.set(rgba.subarray(srcStart, srcStart + width * 4), rowStart + 1);
  }

  // Zlib stream with uncompressed DEFLATE store blocks (max 65535 bytes per block)
  const maxBlock = 65535;
  const blockCount = Math.max(1, Math.ceil(rawScanlines.length / maxBlock));
  const zlibData = new Uint8Array(2 + blockCount * 5 + rawScanlines.length + 4);
  zlibData[0] = 0x78;
  zlibData[1] = 0x01;

  let zOffset = 2;
  for (let b = 0; b < blockCount; b += 1) {
    const start = b * maxBlock;
    const end = Math.min(start + maxBlock, rawScanlines.length);
    const len = end - start;
    const nlen = ~len & 0xffff;
    const isFinal = b === blockCount - 1 ? 1 : 0;
    zlibData[zOffset] = isFinal;
    zlibData[zOffset + 1] = len & 0xff;
    zlibData[zOffset + 2] = (len >>> 8) & 0xff;
    zlibData[zOffset + 3] = nlen & 0xff;
    zlibData[zOffset + 4] = (nlen >>> 8) & 0xff;
    zlibData.set(rawScanlines.subarray(start, end), zOffset + 5);
    zOffset += 5 + len;
  }
  writeUint32BE(zlibData, zOffset, computeAdler32(rawScanlines));

  const ihdr = new Uint8Array(13);
  writeUint32BE(ihdr, 0, width);
  writeUint32BE(ihdr, 4, height);
  ihdr[8] = 8; // Bit depth 8
  ihdr[9] = 6; // Color type 6 (RGBA)
  ihdr[10] = 0;
  ihdr[11] = 0;
  ihdr[12] = 0;

  const signature = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
  const ihdrChunk = makePngChunk('IHDR', ihdr);
  const idatChunk = makePngChunk('IDAT', zlibData);
  const iendChunk = makePngChunk('IEND', new Uint8Array(0));

  const out = new Uint8Array(
    signature.length + ihdrChunk.length + idatChunk.length + iendChunk.length,
  );
  let pos = 0;
  for (const part of [signature, ihdrChunk, idatChunk, iendChunk]) {
    out.set(part, pos);
    pos += part.length;
  }
  return out;
}

export { formatCssHex };
